import {
	DrawerLayout,
	DrawerLayoutProps
} from '@ddc-webplatform/cmsc-components';
import React, { memo, useEffect, useRef, useState } from 'react';
import { DrawerProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { drawerBgColor, drawerWidth, defaultContentSx, defaultFooterSx } from 'constants/ui';

export interface SideDrawerLayoutProps {
	/** Whether the drawer is open */
	open: boolean;
	/** Called when the drawer requests to close */
	onClose: () => void;
	/** Drawer content */
	children: React.ReactNode;

	// ── Header ──────────────────────────────────────────────────────────────
	/** Header title text */
	title: string;
	/** Optional header subtitle */
	subtitle?: string;
	/** Extra headerProps merged on top of title/subtitle (e.g. hideCloseIcon, onCancel, actionButton) */
	headerProps?: DrawerLayoutProps['headerProps'];

	// ── Footer ──────────────────────────────────────────────────────────────
	/** Footer content */
	footer?: React.ReactNode;
	/** Extra props forwarded to DrawerLayout's contentProps (e.g. sx) */
	contentProps?: NonNullable<DrawerLayoutProps['slotProps']>['content'];

	// ── Layout / appearance ─────────────────────────────────────────────────
	/** Drawer width in px – defaults to 350 */
	width?: number;
	/** Paper background color – defaults to '#2c2c2c' */
	bgcolor?: string;

	// ── Secondary drawer positioning ────────────────────────────────────────
	/**
	 * When provided, the drawer behaves as a *secondary* panel:
	 *  – an invisible slide‑container div is rendered at this left offset
	 *  – the drawer paper is shifted right by this amount
	 *  – the backdrop is made invisible
	 *
	 * Omit for a *primary* drawer (e.g. the main sitemap panel).
	 */
	offsetLeft?: number;

	// ── Extras ──────────────────────────────────────────────────────────────
	/** Hide the MUI backdrop entirely (primary drawer pattern) */
	hideBackdrop?: boolean;
	/** Additional SlideProps forwarded to DrawerLayout (e.g. { onExited }) */
	SlideProps?: DrawerProps['SlideProps'];
	/** Extra sx applied to the paper element */
	paperSx?: SxProps<Theme>;
	/** Extra sx applied to the root Drawer */
	sx?: SxProps<Theme>;
}

/**
 * Shared drawer wrapper around `DrawerLayout`.
 *
 * Encapsulates toolbar‑bottom measurement, slide‑container scaffolding,
 * common ModalProps and the recurring dark‑theme styling so that consumers
 * only pass the props that actually differ.
 */
const SideDrawerLayout = ({
	open,
	onClose,
	children,
	title,
	subtitle,
	headerProps: headerPropsProp,
	footer,
	contentProps: contentPropsProp,
	width = drawerWidth,
	bgcolor = drawerBgColor,
	offsetLeft,
	hideBackdrop = false,
	SlideProps: slidePropsProp,
	paperSx,
	sx: sxProp
}: SideDrawerLayoutProps) => {
	// ── Toolbar‑bottom measurement (shared across all usages) ───────────────
	const toolbarBottomRef = useRef(0);
	const [toolbarBottom, setToolbarBottom] = useState(() => {
		const toolbar = document.querySelector(
			'.x-panel-tbar.x-panel-tbar-noheader'
		);
		if (toolbar) {
			const {bottom} = toolbar.getBoundingClientRect();
			toolbarBottomRef.current = bottom;
			return bottom;
		}
		return 0;
	});

	useEffect(() => {
		if (!open) return undefined;
		const measure = () => {
			const toolbar = document.querySelector(
				'.x-panel-tbar.x-panel-tbar-noheader'
			);
			if (toolbar) {
				const { bottom } = toolbar.getBoundingClientRect();
				if (bottom !== toolbarBottomRef.current) {
					toolbarBottomRef.current = bottom;
					setToolbarBottom(bottom);
				}
			}
		};
		measure();
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, [open]);

	// ── Slide container for secondary drawers ───────────────────────────────
	const isSecondary = offsetLeft != null;
	const [slideContainer, setSlideContainer] = useState<HTMLDivElement | null>(
		null
	);

	// Build SlideProps – merge consumer overrides with container when secondary
	const mergedSlideProps: DrawerProps['SlideProps'] = {
		...(isSecondary && slideContainer ? { container: slideContainer } : {}),
		...slidePropsProp
	};

	// ── ModalProps ──────────────────────────────────────────────────────────
	// INTENTIONAL — do NOT remove these without testing in the ExtJS host app.
	// This drawer is embedded in a legacy ExtJS shell that owns its own focus
	// trap and scroll container, which conflicts with MUI's defaults:
	//   disableEnforceFocus  – ExtJS intercepts keyboard events globally; MUI's
	//                          focus lock breaks host toolbar/grid navigation.
	//   disableAutoFocus     – Auto-focusing the drawer steals focus from the
	//                          active ExtJS widget, triggering unwanted blur events.
	//   disableRestoreFocus  – ExtJS manages its own focus stack; MUI restoring
	//                          focus on close causes double-focus and cursor jumps.
	//   disableScrollLock    – ExtJS owns its own scroll; MUI's body overflow:hidden
	//                          shifts the layout and corrupts the host scroll state.
	//   BackdropProps: { invisible: true } (secondary only)
	//                        – Backdrop is kept for click-away-close but hidden to
	//                          avoid dimming the screen over the primary drawer.
	const modalProps: DrawerProps['ModalProps'] = {
		disableEnforceFocus: true,
		disableAutoFocus: true,
		disableRestoreFocus: true,
		disableScrollLock: true,
		...(isSecondary ? { BackdropProps: { invisible: true } } : {})
	};
	const rootSx: SxProps<Theme> = {
		...(isSecondary ? { width, flexShrink: 0 } : {}),
		zIndex: (theme: Theme) => theme.zIndex.drawer - 1,
		...(toolbarBottom > 0 ? { top: `${toolbarBottom}px` } : {}),
		'& .MuiDrawer-paper': {
			width,
			boxSizing: 'border-box',
			pointerEvents: 'auto',
			bgcolor,
			overflowX: 'hidden',
			msOverflowStyle: 'none',
			scrollbarWidth: 'none',
			'&::-webkit-scrollbar': { display: 'none' },
			'& > .MuiBox-root': { bgcolor },
			...(isSecondary ? { borderLeft: 'none', left: `${offsetLeft}px` } : {}),
			zIndex: (theme: Theme) => theme.zIndex.drawer - 1,
			...(toolbarBottom > 0
				? { top: `${toolbarBottom}px`, height: `calc(100% - ${toolbarBottom}px)` }
				: {}),
			'& .cmsc-body': {
				overflowX: 'hidden',
				scrollbarWidth: 'thin',
				scrollbarColor: `#9e9e9e ${drawerBgColor}`,
				'&::-webkit-scrollbar': { width: '8px' },
				'&::-webkit-scrollbar-track': { background: drawerBgColor },
				'&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '3px' },
				'&::-webkit-scrollbar-thumb:hover': { background: '#bdbdbd' },
			},
			...(paperSx as any)
		},
		...(sxProp as any)
	};

	const mergedHeaderProps: DrawerLayoutProps['headerProps'] = {
		title,
		...(subtitle != null ? { subtitle } : {}),
		...headerPropsProp
	};

	return (
		<>
			{isSecondary && (
				<div
					ref={setSlideContainer}
					style={{
						position: 'fixed',
						left: offsetLeft,
						top: 0,
						width: 0,
						height: 0,
						pointerEvents: 'none',
						visibility: 'hidden'
					}}
				/>
			)}
			<DrawerLayout
				headerProps={mergedHeaderProps}
				slotProps={{
					root: {
						anchor: 'left',
						open,
						onClose,
						variant: 'temporary',
						...(hideBackdrop ? { hideBackdrop: true } : {}),
						SlideProps: mergedSlideProps,
						ModalProps: modalProps,
						sx: rootSx
					},
					content: {
						...contentPropsProp,
						sx: {
							...defaultContentSx,
							position: 'relative',
							...contentPropsProp?.sx
						}
					},
					footer: { sx: defaultFooterSx }
				}}
				footer={footer}
			>
				{children}
			</DrawerLayout>
		</>
	);
};

export default memo(SideDrawerLayout);
